import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ContextHolder } from '@frontegg/rest-api';
import { useSetEmbedSession } from '../contexts/PliableAuthContext';
import { UnsavedChangesProvider } from '../contexts/UnsavedChangesContext';
import { SidebarProvider } from '../components/ui/sidebar';
import DashboardDetail from './analysis/DashboardDetail';
import { Chart } from '../components/Chart';
import ElliePage from './Ellie';

type Phase = 'exchanging' | 'ready' | 'error';

/**
 * Embed handoff page.
 *
 * Loaded as an iframe on the customer's site via the URL returned from
 * POST /api/v1/ext/init-session. Exchanges the one-time code for a JWT
 * (which also sets the plb_embed_sid Partitioned cookie), hydrates the
 * PliableAuth context, and renders the target component inline without
 * navigating. Staying on /embed avoids the AuthenticatedRootPage guard
 * (which redirects non-authenticated visits to /login) and the route
 * switch race that flushSync had been working around.
 */
export default function Embed() {
  const [phase, setPhase] = useState<Phase>('exchanging');
  const [message, setMessage] = useState<string>('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [flow, setFlow] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  // Presentation config, fetched from /embed-render-config on chat load. These
  // defaults mirror the backend's so a fetch failure still renders sensibly.
  const [centerInput, setCenterInput] = useState<boolean>(false);
  const [placeholder, setPlaceholder] = useState<string>('Ask a question');
  const [welcomeMessage, setWelcomeMessage] = useState<string>('');
  const setEmbedSession = useSetEmbedSession();
  const queryClient = useQueryClient();
  // React 18 StrictMode runs effects twice in dev — the auth-exchange code
  // is one-time-use so without this guard the second call 401s and the
  // page flips into the error state.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      setPhase('error');
      setMessage('Missing embed code.');
      return;
    }

    (async () => {
      try {
        const resp = await fetch(
          `/api/v1/env/auth-exchange?code=${encodeURIComponent(code)}`,
        );
        if (!resp.ok) {
          setPhase('error');
          setMessage(`Session exchange failed (${resp.status}).`);
          return;
        }
        const data = await resp.json();
        const token: string | undefined = data?.access_token;
        if (!token) {
          setPhase('error');
          setMessage('Session exchange returned no token.');
          return;
        }

        ContextHolder.setAccessToken(token);
        // Decode the JWT to recover flow + target_id (server-minted claims
        // so they can't be tampered with client-side) and the standard
        // user fields for the auth context.
        let payload: any = {};
        try {
          payload = JSON.parse(
            atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
          );
        } catch (e) {
          console.warn('[Embed] JWT decode failed:', e);
          setPhase('error');
          setMessage('Session token is malformed.');
          return;
        }

        // Hydrate the PliableAuth context with the new session — calling
        // the context setter triggers a React re-render with the correct
        // state before the inline target renders. Mirrors to authService
        // so ApiService picks up the Bearer token for outbound API calls.
        setEmbedSession(token, {
          sub: payload.sub || '',
          name: payload.email || payload.sub || '',
          email: payload.email || '',
          tenant_id: payload.tenantId || '',
          roles: (payload.roles || []).map((r: any) =>
            typeof r === 'string' ? r : r.key || '',
          ),
          tenant_name: payload.tenantId || '',
          access_token: token,
          tenants: [],
          // Surface embed_branch so ApiService.getBranchHeader can send the
          // correct Plb-Branch header on subsequent requests — otherwise a
          // DRAFT embed would hit PROD schemas.
          customClaims: { embed_branch: payload.embed_branch || 'PROD' },
        });
        // Any React Query calls that fired before auth was set (e.g.
        // useTenantConfig via FormatLocaleProvider) are stuck in an
        // errored/retrying state with the old `401 no-auth` response
        // cached. Wipe the cache so they refetch fresh with the JWT.
        queryClient.clear();

        const jwtFlow: string | undefined = payload.embed_flow;
        const jwtTargetId: string | undefined = payload.embed_target_id;
        // dashboard + chart require a target; chat is OK without one
        // (lands on a fresh conversation).
        if (jwtFlow === 'dashboard' || jwtFlow === 'chart') {
          if (!jwtTargetId) {
            setPhase('error');
            setMessage(`Missing target for flow: ${jwtFlow}`);
            return;
          }
        } else if (jwtFlow !== 'chat') {
          setPhase('error');
          setMessage(`Unsupported flow: ${jwtFlow ?? '(missing)'}`);
          return;
        }
        setFlow(jwtFlow);
        setTargetId(jwtTargetId || null);
        // Chat-only: server-minted claim that toggles the history sidebar.
        setShowHistory(jwtFlow === 'chat' && payload.embed_show_history === true);
        // Chat-only: front-end presentation config (input centering, etc.)
        // lives in the DB rather than the JWT, so the token stays lean as that
        // config grows. Fetch it once here; on any failure the defaults (set in
        // useState) apply, so the chat still renders.
        if (jwtFlow === 'chat') {
          try {
            const cfgResp = await fetch('/api/v1/ext/embed-render-config', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (cfgResp.ok) {
              const cfg = await cfgResp.json();
              setCenterInput(cfg?.center_input_on_new_chat === true);
              if (typeof cfg?.placeholder === 'string') setPlaceholder(cfg.placeholder);
              if (typeof cfg?.welcome_message === 'string') setWelcomeMessage(cfg.welcome_message);
            }
          } catch (e) {
            console.warn('[Embed] render-config fetch failed:', e);
          }
        }
        setPhase('ready');
      } catch (e: any) {
        console.error('[Embed] exchange error', e);
        setPhase('error');
        setMessage('Unable to start embed session.');
      }
    })();
  }, [setEmbedSession]);

  if (phase === 'ready') {
    // Render the target inline — iframe URL stays at /embed but the user
    // sees the target view. AuthenticatedRootPage (and its login redirect)
    // is bypassed entirely. We still need to supply the providers each
    // target component depends on — normally mounted by
    // AuthenticatedRootPage around the <Outlet />.
    let content: ReactNode = null;
    if (flow === 'dashboard' && targetId) {
      content = <DashboardDetail embeddedDashboardId={targetId} />;
    } else if (flow === 'chart' && targetId) {
      // Use the read-only Chart component (same as dashboards/Ellie) rather
      // than the ChartDetail editor page. Embed is display-only so we
      // suppress save/add-to-dashboard affordances; auto-prep stays on so
      // the data-404 → /prep fallback kicks in for filter combos the
      // customer-scoped embed generates that haven't been built yet.
      content = (
        <div className="h-screen w-screen p-4">
          <Chart
            analysisId={targetId}
            disableSave
            disableAddToDashboard
            hideToolbar
          />
        </div>
      );
    } else if (flow === 'chat') {
      // target_id carries the thread pre-created at init-session time with
      // the customer's chat_config — loading it (rather than generating a
      // client-side id) is what makes those settings apply.
      content = <ElliePage embedThreadId={targetId || undefined} showHistory={showHistory} centerInputOnNewChat={centerInput} placeholder={placeholder} welcomeMessage={welcomeMessage} />;
    }
    if (content) {
      return (
        <SidebarProvider>
          <UnsavedChangesProvider>{content}</UnsavedChangesProvider>
        </SidebarProvider>
      );
    }
  }

  // Blank while the code exchange is in flight — it resolves quickly and a
  // "Loading…" flash looks broken inside the customer's iframe.
  if (phase !== 'error') {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center text-sm text-gray-600">
        <p className="font-medium text-red-600 mb-1">Embed session error</p>
        <p>{message}</p>
      </div>
    </div>
  );
}
